import { action, computed, makeObservable, observable } from 'mobx'

import { hasContent, PhotoMarkup } from './photoMarkupModel'

const MAX_ENTRIES = 50
const CAPTURE_DELAY_MS = 250

export default class PhotoMarkupHistory {
  @observable.ref private entries: PhotoMarkup[][] = [[]]
  @observable private index = 0
  @observable private hasPendingCapture = false
  private headJson = '[]'
  private captureTimer: ReturnType<typeof setTimeout> = null

  public constructor(private readonly read: () => PhotoMarkup[]) {
    makeObservable(this)
  }

  @computed
  public get canUndo(): boolean {
    return this.index > 0 || this.hasPendingCapture
  }

  @computed
  public get canRedo(): boolean {
    return !this.hasPendingCapture && this.index < this.entries.length - 1
  }

  @action.bound
  public capture(): void {
    clearTimeout(this.captureTimer)
    this.hasPendingCapture = true
    this.captureTimer = setTimeout(this.commit, CAPTURE_DELAY_MS)
  }

  @action.bound
  public commit(): void {
    this.cancelPending()
    const snapshot = this.read().filter(hasContent)
    const json = JSON.stringify(snapshot)
    if (json === this.headJson) return
    this.entries = [...this.entries.slice(0, this.index + 1), snapshot].slice(
      -MAX_ENTRIES,
    )
    this.index = this.entries.length - 1
    this.headJson = json
  }

  @action.bound
  public undo(): PhotoMarkup[] | null {
    if (this.hasPendingCapture) this.commit()
    if (this.index <= 0) return null
    return this.moveTo(this.index - 1)
  }

  @action.bound
  public redo(): PhotoMarkup[] | null {
    if (this.hasPendingCapture) this.commit()
    if (this.index >= this.entries.length - 1) return null
    return this.moveTo(this.index + 1)
  }

  @action.bound
  public dispose(): void {
    this.cancelPending()
  }

  private moveTo(index: number): PhotoMarkup[] {
    this.index = index
    const snapshot = this.entries[index]
    this.headJson = JSON.stringify(snapshot)
    return snapshot
  }

  @action
  private cancelPending(): void {
    clearTimeout(this.captureTimer)
    this.captureTimer = null
    this.hasPendingCapture = false
  }
}
